/**
 * fatt-calc.js — MOTORE DI CALCOLO FatturaPA (funzioni PURE, zero stato, zero DB).
 *
 * Implementa la sezione "Regole del motore" di docs/fatturapa/CONTRATTO-MVP.md, con le
 * formule/tolleranze dei controlli SdI 00421/00422/00423 (docs/fatturapa/REFERENCE.md §4).
 * La STESSA lib viene importata dal client (calc-client.js) per l'anteprima live: qui non
 * deve entrare nulla di server-only (niente db, niente require di route).
 *
 * Convenzioni:
 * - NC (TD04/TD05): gli importi restano POSITIVI — il segno lo dà il TipoDocumento nell'XML.
 * - Tutti gli output monetari passano da r2(); i prezzi unitari restano a precisione piena
 *   (fino a 8 decimali) DENTRO la cascata sconti, si arrotonda solo il risultato finale.
 * - Immutabilità: ricalcolaFattura NON tocca il record in ingresso, ritorna un record NUOVO
 *   (righe e riepiloghi sono array/oggetti nuovi; le sotto-strutture non ricalcolate restano
 *   condivise per riferimento, mai modificate).
 */

// Nature soggette a imposta di bollo (contratto §Regole del motore, punto 6 / piano §2).
// Le altre (N3.1..N3.4, N5, N6.*, N7) NON concorrono alla soglia; la natura vuota ('') è
// un'operazione con IVA e per definizione non entra nel bollo.
const NATURE_BOLLO_SOGGETTE = new Set(['N1', 'N2.1', 'N2.2', 'N3.5', 'N3.6', 'N4']);
const SOGLIA_BOLLO = 77.47; // bollo dovuto SOPRA la soglia (strettamente >)

// ---------------------------------------------------------------------------
// r2 — arrotondamento matematico a 2 decimali: half-up sulla 3ª cifra (giù se <5, su se ≥5),
// segno preservato (sui negativi arrotonda "away from zero": r2(-1.005) = -1.01).
// Il passaggio da toPrecision(15) SNAPPA il rumore binario del double alla sua forma decimale
// (es. 1.005*100 = 100.49999999999999 → 100.5) così il confine .005 arrotonda DAVVERO in su,
// come richiede lo scarto SdI 00421; 15 cifre significative bastano per gli 11 interi + 2
// decimali di Amount2DecimalType senza mangiare cifre vere.
// ---------------------------------------------------------------------------
function r2(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return 0;
  const sign = n < 0 ? -1 : 1;
  const scaled = Number((Math.abs(n) * 100).toPrecision(15));
  return sign * (Math.round(scaled) / 100);
}

// Applica gli sconti/maggiorazioni IN CASCATA al prezzo unitario (regola 00423):
// - SC percentuale: prezzo × (1 − p/100) · MG percentuale: prezzo × (1 + p/100);
// - SC importo: sottrazione · MG importo: somma;
// - in sequenza nell'ordine dell'array: ogni sconto lavora sul risultato del precedente.
// Se un elemento ha SIA percentuale SIA importo vince la percentuale (se ≠ 0): interpretazione
// più semplice dell'"almeno uno dei due" dell'XSD, dichiarata nelle note dell'implementazione.
// Elemento senza percentuale né importo → no-op (lo segnala il validatore: 00437/00438).
function _applicaSconti(prezzo, sconti) {
  let p = prezzo;
  for (const s of (sconti || [])) {
    if (!s) continue;
    const segno = s.tipo === 'MG' ? 1 : -1; // tutto ciò che non è MG si tratta da SC
    const perc = (s.percentuale == null || s.percentuale === '') ? null : Number(s.percentuale);
    const imp = (s.importo == null || s.importo === '') ? null : Number(s.importo);
    if (perc != null && Number.isFinite(perc) && perc !== 0) p = p * (1 + segno * perc / 100);
    else if (imp != null && Number.isFinite(imp)) p = p + segno * imp;
  }
  return p;
}

// ---------------------------------------------------------------------------
// calcRiga — regola 2 del contratto (scarto SdI 00423, tolleranza ±1 cent):
//   prezzoTotale = r2((prezzoUnitario ± sconti in cascata) × (quantita ?? 1))
// Quantita assente/null/'' = 1 (come assume SdI); quantita 0 esplicita resta 0.
// Ritorna una riga NUOVA (copia shallow) con prezzoTotale valorizzato: mai mutare l'input.
// ---------------------------------------------------------------------------
function calcRiga(riga) {
  const r = riga || {};
  const prezzo = Number(r.prezzoUnitario) || 0;
  const qta = (r.quantita == null || r.quantita === '') ? 1 : (Number(r.quantita) || 0);
  const prezzoTotale = r2(_applicaSconti(prezzo, r.sconti) * qta);
  return Object.assign({}, r, { prezzoTotale });
}

// ---------------------------------------------------------------------------
// Riepiloghi IVA — regola 3 del contratto (scarti SdI 00419/00422, tolleranza ±1 €):
// un blocco per OGNI combinazione (aliquotaIva, natura) presente in righe + casse;
//   imponibile = r2(Σ prezzoTotale righe + Σ importo casse della stessa combinazione)
//   imposta    = r2(aliquota × imponibile / 100)        ← half-up 3ª cifra (00421)
//   esigibilita = 'S' se il cliente è in split payment, altrimenti '' (omessa nell'XML).
// Natura null/undefined e stringa vuota sono la STESSA combinazione (nessuna natura).
// Ordine deterministico: aliquota DECRESCENTE (22, 10, 0…), a parità natura crescente.
// ---------------------------------------------------------------------------
function _riepiloghi(righe, casse, splitPayment) {
  const acc = new Map();
  const aggiungi = (aliquotaRaw, naturaRaw, importo) => {
    const aliquotaIva = Number(aliquotaRaw) || 0;
    const natura = (naturaRaw == null) ? '' : String(naturaRaw);
    const k = aliquotaIva + '|' + natura;
    let g = acc.get(k);
    if (!g) { g = { aliquotaIva, natura, somma: 0 }; acc.set(k, g); }
    g.somma += Number(importo) || 0;
  };
  for (const r of (righe || [])) aggiungi(r.aliquotaIva, r.natura, r.prezzoTotale);
  for (const c of (casse || [])) { if (c) aggiungi(c.aliquotaIva, c.natura, c.importo); }
  const out = [...acc.values()].map((g) => {
    const imponibile = r2(g.somma);
    return {
      aliquotaIva: g.aliquotaIva, natura: g.natura,
      esigibilita: splitPayment ? 'S' : '',
      speseAccessorie: null, arrotondamento: null,
      imponibile, imposta: r2(g.aliquotaIva * imponibile / 100),
      riferimentoNormativo: '',
    };
  });
  out.sort((a, b) => (b.aliquotaIva - a.aliquotaIva) || String(a.natura).localeCompare(String(b.natura)));
  return out;
}

// ---------------------------------------------------------------------------
// ricalcolaFattura — regole 3..6 del contratto. Prende il record (shape CONTRATTO-MVP §SHAPE)
// e ritorna un record NUOVO con tutti i campi calcolati valorizzati:
//   righe[].prezzoTotale, riepiloghi, totaleImponibile, totaleImposta, totaleDocumento,
//   totaleRitenute, nettoAPagare, bolloProposto.
// opts: { splitPayment: boolean } — arriva dal CLIENTE (fatt_clienti.split_payment), non dal record.
//
// MVP (contratto, regola 4): totaleDocumento = r2(totaleImponibile + totaleImposta +
// bollo.importo se presente + arrotondamento). Gli scontiDoc NON entrano nel totale MVP.
// Le casse NON si sommano a parte: sono GIÀ dentro gli imponibili dei riepiloghi.
//
// Bollo PER NATURA (regola 6): la soglia 77.47 si confronta con la SOMMA dei soli imponibili
// dei riepiloghi con natura soggetta (N1, N2.1, N2.2, N3.5, N3.6, N4) — un documento misto
// con quota esente sotto soglia NON propone il bollo anche se il totale documento la supera,
// e un reverse charge N6.x non lo propone MAI.
// ---------------------------------------------------------------------------
function ricalcolaFattura(record, opts) {
  const rec = record || {};
  const split = !!(opts && opts.splitPayment);
  const dg = rec.datiGenerali || {};

  const righe = (rec.righe || []).map(calcRiga);
  const riepiloghi = _riepiloghi(righe, dg.casse, split);

  const totaleImponibile = r2(riepiloghi.reduce((s, x) => s + x.imponibile, 0));
  const totaleImposta = r2(riepiloghi.reduce((s, x) => s + x.imposta, 0));
  const bolloImporto = (dg.bollo && dg.bollo.importo != null) ? (Number(dg.bollo.importo) || 0) : 0;
  const arrotondamento = Number(dg.arrotondamento) || 0;
  const totaleDocumento = r2(totaleImponibile + totaleImposta + bolloImporto + arrotondamento);

  const totaleRitenute = r2((dg.ritenute || []).reduce((s, x) => s + (Number(x && x.importo) || 0), 0));
  // Split payment: l'IVA scissa non transita dal fornitore → si sottrae dal netto (regola 5).
  const nettoAPagare = r2(totaleDocumento - totaleRitenute - (split ? totaleImposta : 0));

  const imponibileSoggettoBollo = r2(riepiloghi
    .filter((x) => NATURE_BOLLO_SOGGETTE.has(x.natura))
    .reduce((s, x) => s + x.imponibile, 0));
  const bolloProposto = imponibileSoggettoBollo > SOGLIA_BOLLO;

  return Object.assign({}, rec, {
    righe, riepiloghi,
    totaleImponibile, totaleImposta, totaleDocumento,
    totaleRitenute, nettoAPagare, bolloProposto,
  });
}

module.exports = { r2, calcRiga, ricalcolaFattura };
